import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { Erc20PaymentProcessingModule } from "./Erc20PaymentProcessingModule";
import { SelectPaymentMethodPopupScene } from "./scenes/SelectPaymentMethodPopupScene";
import { PaymentProcessingPopupSceneOptions } from "./scenes/PaymentProcessingPopupSceneOptions";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { PaymentResult } from "../../../../web3/PaymentResult";
import { PaymentProcessingType } from "./type/PaymentProcessingType";
import { Web3Model } from "../../../../web3/models/Web3Model";
import { CancellationTokenSource } from "../../../../shared/async/CancellationTokenSource";
import { CancellationToken } from "../../../../shared/async/CancellationToken";

type SelectPaymentPopupSceneFactory = (options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>, cancellationToken: CancellationToken) => Promise<SelectPaymentMethodPopupScene>;

@injectable()
export class Erc20PaymentProcessingModuleFlowExecutor extends ModuleFlowExecutor<Erc20PaymentProcessingModule, PaymentResult> {
    private topPanelOnNotificationAfterExecute: boolean;
    private readonly cancellationTokenSource: CancellationTokenSource;

    public constructor(
        private readonly sceneManager: SceneManager,
        private readonly web3Model: Web3Model,
        private readonly asyncOperationNotification: AsyncOperationNotification,
        private readonly topPanel: TopPanel,
        @inject("ILogger")
        private readonly logger: ILogger,
    ) {
        super();
        this.sceneManager = sceneManager;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.cancellationTokenSource = new CancellationTokenSource();
    }

    public override async execute(module: Erc20PaymentProcessingModule, options?: PaymentProcessingPopupSceneOptions<PaymentProcessingType>): Promise<PaymentResult> {
        const token = this.cancellationTokenSource.token;
        try {
            const erc20PaymentProcessingFactory: SelectPaymentPopupSceneFactory = await module.resolve("SelectPaymentPopupSceneFactory");
            this.asyncOperationNotification.show();
            await this.web3Model.updateErc20Balance(options.chain, options.erc20Address);
            
            const scene = await erc20PaymentProcessingFactory(options, token);
            this.asyncOperationNotification.hide();

            this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToOriginParent(true);
            }

            const sceneResult = await this.sceneManager.showViewableAsync(scene);
            return sceneResult;
        }
        catch (e: unknown) {
            this.logger.error(`Exception on executing Erc20 Payment Flow: `, e);
            return PaymentResult.FAILURE;
        } finally {
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront(true);
            }
            else {
                this.topPanel.bringToOriginParent(true);
            }
            this.cancellationTokenSource.cancel();
            this.cancellationTokenSource.dispose();
            this.asyncOperationNotification.hide();
        }
    }
}
